import { CandidatLink } from './candidat/CandidatLink';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { qcmTypeLabel } from '../lib/qcmTypes';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { QcmPdfButtons } from './qcm/QcmPdfButtons';
import { VoirResultatsButton } from './qcm/GrilleResultats';
import {
  FileText, Send, QrCode, CheckCircle2, XCircle, Users, Copy, ExternalLink, RefreshCw,
  ClipboardCheck, PenLine, Save, KeyRound, ShieldCheck, Target
} from 'lucide-react';
import { getAuthToken } from '../lib/authToken';
import { RepasserButton } from './suivi/RepasserDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL;

const EVAL_TYPE_LABELS = {
  exercice: 'EXO',
  cas_pratique: 'CAS',
  application: 'APP',
  examen: 'EXA',
  atelier: 'ATE',
  certification: 'CERT',
};

const EVAL_TYPE_COLORS = {
  exercice: 'bg-blue-100 text-blue-700',
  cas_pratique: 'bg-amber-100 text-amber-700',
  application: 'bg-purple-100 text-purple-700',
  examen: 'bg-red-100 text-red-700',
  atelier: 'bg-emerald-100 text-emerald-700',
  certification: 'bg-rose-100 text-rose-700',
};

const VALEUR_OPTIONS = [
  { value: 'NA', label: 'NA', pct: 0, color: 'bg-red-100 text-red-700 border-red-300', active: 'bg-red-500 text-white border-red-600' },
  { value: 'ECA', label: 'ECA', pct: 50, color: 'bg-amber-100 text-amber-700 border-amber-300', active: 'bg-amber-500 text-white border-amber-600' },
  { value: 'A', label: 'A', pct: 100, color: 'bg-emerald-100 text-emerald-700 border-emerald-300', active: 'bg-emerald-500 text-white border-emerald-600' },
];

// ============ Generic Competency Grading Table ============
const GradingTable = ({ competences, candidats, loadGrades, saveCandidat, testId, noteMax }) => {
  const [grades, setGrades] = useState({});
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState({});
  const [recos, setRecos] = useState({});
  const [disabledMap, setDisabledMap] = useState({});
  const [infos, setInfos] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    let mounted = true;
    loadGrades().then((data) => {
      if (!mounted) return;
      setGrades(data.grades || {});
      setScores(data.scores || {});
      setNotes(data.notes || {});
      setRecos(data.recos || {});
      setDisabledMap(data.disabled || {});
      setInfos(data.infos || {});
    }).catch(() => {});
    return () => { mounted = false; };
  }, [loadGrades]);

  const setGrade = (candidatId, compId, valeur) => {
    setGrades(prev => ({
      ...prev,
      [candidatId]: { ...(prev[candidatId] || {}), [compId]: valeur },
    }));
  };

  const doSave = async (candidat) => {
    const cid = candidat.candidat_id || candidat.id;
    if (noteMax && (notes[cid] === '' || notes[cid] == null)) { toast.error('Saisissez la note obtenue'); return; }
    const candidatGrades = grades[cid] || {};
    setSaving(prev => ({ ...prev, [cid]: true }));
    const resultats = competences.map(c => {
      const valeur = candidatGrades[c.id] || 'NA';
      const pct = VALEUR_OPTIONS.find(v => v.value === valeur)?.pct || 0;
      return { competence_id: c.id, valeur, pourcentage: pct };
    });
    try {
      const data = await saveCandidat(candidat, resultats, recos[cid] || '', noteMax ? parseFloat(notes[cid]) : undefined);
      toast.success(`Notation enregistree pour ${candidat.nom || ''} ${candidat.prenom || ''}`);
      (data?.validations_creees || []).forEach(v => {
        toast.success(`🎉 Module ${v.module_code || v.module_titre} valide automatiquement pour ${v.candidat_nom || `${candidat.nom || ''} ${candidat.prenom || ''}`.trim()}`, { duration: 6000 });
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    }
    setSaving(prev => ({ ...prev, [cid]: false }));
  };

  const saveAll = async () => {
    for (const c of candidats) {
      const cid = c.candidat_id || c.id;
      if (disabledMap[cid]) continue;
      await doSave(c);
    }
  };

  const toutAcquis = () => {
    setGrades(prev => {
      const next = { ...prev };
      for (const c of candidats) {
        const cid = c.candidat_id || c.id;
        if (disabledMap[cid]) continue;
        next[cid] = Object.fromEntries(competences.map(comp => [comp.id, 'A']));
      }
      return next;
    });
    toast.info('« Acquis » appliqué à tous les candidats évaluables — pensez à enregistrer');
  };

  if (competences.length === 0 && !noteMax) {
    return <p className="text-xs text-slate-400 text-center py-2">Aucun savoir-faire / connaissance défini pour cette évaluation</p>;
  }

  return (
    <div className="space-y-3" data-testid={`grading-table-${testId}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left p-2 border border-slate-200 font-semibold text-slate-600 min-w-[150px] sticky left-0 bg-slate-50 z-10">Candidat</th>
              <th className="p-2 border border-slate-200 font-medium text-slate-600 text-center w-[80px]">{noteMax ? `Note /${noteMax}` : 'Note obtenue'}</th>
              {competences.map(c => (
                <th key={c.id} className="p-2 border border-slate-200 font-medium text-slate-600 text-center min-w-[100px]" title={c.titre}>
                  <span className="line-clamp-2">{c.titre}</span>
                </th>
              ))}
              <th className="p-2 border border-slate-200 font-medium text-slate-600 text-left min-w-[180px]">Recommandations</th>
              <th className="p-2 border border-slate-200 font-semibold text-slate-600 text-center w-[70px]">Action</th>
            </tr>
          </thead>
          <tbody>
            {candidats.map(candidat => {
              const cid = candidat.candidat_id || candidat.id;
              const candidatGrades = grades[cid] || {};
              const allGraded = competences.every(c => candidatGrades[c.id]);
              const rowDisabled = disabledMap[cid];

              return (
                <tr key={cid} className={rowDisabled ? 'opacity-50' : 'hover:bg-slate-50/50'}>
                  <td className="p-2 border border-slate-200 font-medium text-slate-700 sticky left-0 bg-white z-10">
                    {candidat.nom || ''} {candidat.prenom || ''}
                    {rowDisabled && <p className="text-[9px] text-amber-600 font-normal italic">{rowDisabled}</p>}
                    {infos[cid] && (
                      <p className="text-[9px] font-normal mt-0.5 leading-tight" data-testid={`tentative-info-${cid}`}>
                        {infos[cid].autreJournee
                          ? <span className="text-indigo-700">Nouvelle saisie = <strong>tentative {infos[cid].nb + 1}</strong> · précédente{infos[cid].nb > 1 ? 's' : ''} : {infos[cid].historique.map(h => `${h.moyenne ?? '—'} % (${(h.date_journee || '').split('-').reverse().join('/')})`).join(', ')}</span>
                          : infos[cid].nb > 1
                            ? <span className="text-slate-500">Tentative {infos[cid].tentative}/{infos[cid].nb} · historique : {infos[cid].historique.map(h => `${h.moyenne ?? '—'} %${h.retenu ? ' ★' : ''}`).join(' → ')}</span>
                            : <span className="text-slate-400">Noté le {(infos[cid].date_journee || '').split('-').reverse().join('/')} — modifier ici corrige la tentative en cours</span>}
                      </p>
                    )}
                  </td>
                  <td className="p-1.5 border border-slate-200 text-center" data-testid={`score-${cid}`}>
                    {noteMax ? (
                      <input type="number" min="0" step="0.5" value={notes[cid] ?? ''} disabled={!!rowDisabled} onChange={(e) => setNotes(prev => ({ ...prev, [cid]: e.target.value }))} className={`w-16 h-7 text-xs text-center border rounded ${parseFloat(notes[cid]) > noteMax ? 'border-indigo-400 bg-indigo-50 font-semibold' : ''}`} title="Une note supérieure au barème est acceptée (pourcentage > 100 %)" data-testid={`note-input-${cid}`} />
                    ) : (
                      scores[cid] != null ? <span className={`font-bold ${scores[cid] >= 70 ? 'text-emerald-600' : scores[cid] >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{scores[cid]}%</span> : <span className="text-slate-300">—</span>
                    )}
                    {noteMax && notes[cid] !== '' && notes[cid] != null && <p className={`text-[9px] ${parseFloat(notes[cid]) > noteMax ? 'text-indigo-600 font-semibold' : 'text-slate-400'}`} data-testid={`note-pct-${cid}`}>{Math.round(parseFloat(notes[cid]) / noteMax * 100)}%</p>}
                  </td>
                  {competences.map(c => {
                    const currentVal = candidatGrades[c.id];
                    return (
                      <td key={c.id} className="p-1.5 border border-slate-200 text-center">
                        <div className="flex gap-0.5 justify-center">
                          {VALEUR_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => !rowDisabled && setGrade(cid, c.id, opt.value)}
                              disabled={!!rowDisabled}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold border transition-all ${
                                currentVal === opt.value ? opt.active : 'bg-white text-slate-300 border-slate-200 hover:border-slate-400'
                              }`}
                              data-testid={`grade-${cid}-${c.id}-${opt.value}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                  <td className="p-1.5 border border-slate-200">
                    <input
                      type="text"
                      value={recos[cid] || ''}
                      onChange={(e) => setRecos(prev => ({ ...prev, [cid]: e.target.value }))}
                      disabled={!!rowDisabled}
                      placeholder="Recommandations..."
                      className="w-full text-[10px] px-1.5 py-1 border border-slate-200 rounded focus:outline-none focus:border-indigo-400 disabled:bg-slate-50"
                      data-testid={`reco-${cid}`}
                    />
                  </td>
                  <td className="p-1.5 border border-slate-200 text-center">
                    <Button
                      size="sm"
                      variant={allGraded ? "default" : "outline"}
                      className={`h-6 text-[10px] ${allGraded ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                      onClick={() => doSave(candidat)}
                      disabled={saving[cid] || !!rowDisabled}
                      data-testid={`save-grade-${cid}`}
                    >
                      {saving[cid] ? '...' : <Save className="w-3 h-3" />}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex gap-3 text-[10px] text-slate-400">
          <span><span className="inline-block w-2 h-2 rounded bg-red-500 mr-1"></span>NA = Non Acquis</span>
          <span><span className="inline-block w-2 h-2 rounded bg-amber-500 mr-1"></span>ECA = En Cours d'Acquisition</span>
          <span><span className="inline-block w-2 h-2 rounded bg-emerald-500 mr-1"></span>A = Acquis</span>
        </div>
        <div className="flex items-center gap-2">
          {competences.length > 0 && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={toutAcquis} data-testid={`tout-acquis-${testId}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Tout acquis
            </Button>
          )}
          <Button size="sm" className="h-7 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700" onClick={saveAll} data-testid="save-all-grades">
            <Save className="w-3.5 h-3.5" /> Tout enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

const BAREMES = [100, 20, 10];

// ============ Eval Manuelle grading wrapper ============
const EvalGradingTable = ({ evaluation, dossierId, dateJournee, candidats, headers, onSaved }) => {
  const modeNoteFixe = evaluation.mode_notation === 'note';
  const [bareme, setBareme] = useState(modeNoteFixe ? (evaluation.note_max || 20) : 100);
  const [baremeLibre, setBaremeLibre] = useState(false);
  const loadGrades = useCallback(async () => {
    const res = await axios.get(`${API_URL}/evaluations-manuelles/${evaluation.id}/resultats-dossier/${dossierId}`, { headers, params: dateJournee ? { date_journee: dateJournee } : {} });
    const grades = {}; const recos = {}; const scores = {}; const notes = {}; const infos = {};
    let existingMax = null;
    for (const p of (res.data.passages || [])) {
      const autreJournee = !!(p.date_journee && dateJournee && p.date_journee !== dateJournee);
      infos[p.candidat_id] = { tentative: p.tentative || 1, nb: p.nb_tentatives || 1, date_journee: p.date_journee, autreJournee, historique: p.historique || [] };
      if (p.note_max) existingMax = p.note_max;
      if (autreJournee) continue; // autre journée : la nouvelle saisie crée une nouvelle tentative, grille vierge
      const g = {};
      for (const r of (p.resultats || [])) if (r.valeur) g[r.competence_id] = r.valeur;
      grades[p.candidat_id] = g;
      scores[p.candidat_id] = p.moyenne ?? null;
      if (p.note != null) notes[p.candidat_id] = p.note;
      recos[p.candidat_id] = p.recommandations || '';
    }
    if (!modeNoteFixe && existingMax) { setBareme(existingMax); if (!BAREMES.includes(existingMax)) setBaremeLibre(true); }
    return { grades, recos, disabled: {}, scores, notes, infos };
  }, [evaluation.id, dossierId, dateJournee, headers, modeNoteFixe]);

  const saveCandidat = useCallback(async (candidat, resultats, recommandations, note) => {
    const cid = candidat.candidat_id || candidat.id;
    const res = await axios.post(`${API_URL}/evaluations-manuelles/${evaluation.id}/noter`, {
      candidat_id: cid,
      dossier_id: dossierId,
      date_journee: dateJournee || '',
      candidat_nom: `${candidat.nom || ''} ${candidat.prenom || ''}`.trim(),
      resultats: resultats.filter(r => r.valeur),
      recommandations,
      note,
      ...(modeNoteFixe ? {} : { note_max: bareme }),
    }, { headers });
    if (onSaved) onSaved();
    return res.data;
  }, [evaluation.id, dossierId, dateJournee, headers, onSaved, modeNoteFixe, bareme]);

  return (
    <div className="space-y-2">
      {!modeNoteFixe && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`bareme-${evaluation.id}`}>
          <span className="text-slate-500 font-medium">Barème de notation :</span>
          {BAREMES.map(b => (
            <button key={b} type="button" onClick={() => { setBareme(b); setBaremeLibre(false); }} data-testid={`bareme-${evaluation.id}-${b}`}
              className={`px-2 py-0.5 rounded border font-semibold ${!baremeLibre && bareme === b ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}>/{b}</button>
          ))}
          <button type="button" onClick={() => setBaremeLibre(true)} data-testid={`bareme-${evaluation.id}-autre`}
            className={`px-2 py-0.5 rounded border font-semibold ${baremeLibre ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}>Autre</button>
          {baremeLibre && <input type="number" min="1" step="1" value={bareme} onChange={(e) => setBareme(parseFloat(e.target.value) || 0)} className="w-16 h-6 text-xs text-center border rounded" data-testid={`bareme-${evaluation.id}-input`} />}
          <span className="text-slate-400">La note est convertie en % pour les bilans (ex. {Math.round(bareme / 2 * 10) / 10}/{bareme} = 50 %). Une note au-dessus du barème est acceptée (ex. {Math.round(bareme * 1.17)}/{bareme} = 117 %).</span>
        </div>
      )}
      <GradingTable competences={evaluation.competences_visees || []} candidats={candidats} loadGrades={loadGrades} saveCandidat={saveCandidat} testId={evaluation.id} noteMax={bareme || undefined} />
    </div>
  );
};

// ============ Création d'une évaluation libre par le formateur ============
const EvaluationLibreForm = ({ dossierId, dateJournee, moduleOptions, onCreated, onClose }) => {
  const [form, setForm] = useState({ titre: '', objectif: '', type_evaluation: 'exercice', module_id: moduleOptions[0]?.id || '', note_max: 20, competences: [''] });
  const [saving, setSaving] = useState(false);
  const setComp = (i, v) => setForm(f => ({ ...f, competences: f.competences.map((c, j) => j === i ? v : c) }));
  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/portail/formateur/evaluation-libre`, { ...form, dossier_id: dossierId, date_journee: dateJournee || null, note_max: parseFloat(form.note_max) || 20, competences: form.competences.filter(c => c.trim()) });
      toast.success('Évaluation créée — vous pouvez saisir les résultats'); onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  return (
    <Card className="border border-indigo-200 shadow-sm" data-testid="eval-libre-form">
      <CardContent className="p-3 space-y-2 text-xs">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <label className="space-y-0.5 md:col-span-2"><span className="text-slate-500">Intitulé *</span><input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="w-full h-8 border rounded px-2" placeholder="Ex. : Cas pratique gerbage en hauteur" data-testid="eval-libre-titre" /></label>
          <label className="space-y-0.5 md:col-span-2"><span className="text-slate-500">Objectif</span><textarea rows={2} value={form.objectif} onChange={(e) => setForm({ ...form, objectif: e.target.value })} className="w-full border rounded px-2 py-1" placeholder="Ce que l'évaluation vérifie" data-testid="eval-libre-objectif" /></label>
          <label className="space-y-0.5"><span className="text-slate-500">Type</span>
            <select value={form.type_evaluation} onChange={(e) => setForm({ ...form, type_evaluation: e.target.value })} className="w-full h-8 border rounded px-2 bg-white" data-testid="eval-libre-type"><option value="exercice">Exercice</option><option value="cas_pratique">Cas pratique</option><option value="application">Application</option><option value="examen">Examen</option><option value="atelier">Atelier</option><option value="certification">Test / Certification (non repassable)</option></select></label>
          <label className="space-y-0.5"><span className="text-slate-500">Note maximale</span><input type="number" min="1" value={form.note_max} onChange={(e) => setForm({ ...form, note_max: e.target.value })} className="w-full h-8 border rounded px-2" data-testid="eval-libre-note-max" /></label>
          {moduleOptions.length > 1 && <label className="space-y-0.5 md:col-span-2"><span className="text-slate-500">Module rattaché</span>
            <select value={form.module_id} onChange={(e) => setForm({ ...form, module_id: e.target.value })} className="w-full h-8 border rounded px-2 bg-white" data-testid="eval-libre-module">{moduleOptions.map(m => <option key={m.id} value={m.id}>{m.code} {m.titre}</option>)}</select></label>}
        </div>
        <div className="space-y-1">
          <span className="text-slate-500">Savoir-faire et connaissances évalués</span>
          {form.competences.map((c, i) => <input key={i} value={c} onChange={(e) => setComp(i, e.target.value)} className="w-full h-7 border rounded px-2" placeholder={`Savoir-faire / connaissance ${i + 1}`} data-testid={`eval-libre-comp-${i}`} />)}
          <button type="button" onClick={() => setForm(f => ({ ...f, competences: [...f.competences, ''] }))} className="text-indigo-600 text-[11px]" data-testid="eval-libre-add-comp">+ Ajouter un savoir-faire / une connaissance</button>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={onClose}>Annuler</Button>
          <Button type="button" size="sm" className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" disabled={saving || !form.titre.trim() || !form.module_id} onClick={submit} data-testid="eval-libre-submit">Créer l'évaluation</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ QCM competencies grading wrapper ============
const QcmGradingTable = ({ qcm, dossierId, dateJournee, candidats, headers, onSaved }) => {
  const loadGrades = useCallback(async () => {
    const res = await axios.get(`${API_URL}/qcms/${qcm.id}/resultats-dossier/${dossierId}`, { headers, params: dateJournee ? { date_journee: dateJournee } : {} });
    const grades = {}; const recos = {}; const disabled = {}; const scores = {};
    const passedIds = new Set();
    for (const p of (res.data.passages || [])) {
      if (!p.candidat_id) continue;
      passedIds.add(p.candidat_id);
      const g = {};
      for (const r of (p.competences_resultats || [])) g[r.competence_id] = r.valeur;
      grades[p.candidat_id] = g;
      scores[p.candidat_id] = p.pourcentage ?? null;
      recos[p.candidat_id] = p.recommandations || '';
    }
    for (const c of candidats) {
      const cid = c.candidat_id || c.id;
      if (!passedIds.has(cid)) disabled[cid] = "N'a pas encore passé le QCM";
    }
    return { grades, recos, disabled, scores };
  }, [qcm.id, dossierId, dateJournee, headers, candidats]);

  const saveCandidat = useCallback(async (candidat, resultats, recommandations) => {
    const cid = candidat.candidat_id || candidat.id;
    const res = await axios.post(`${API_URL}/qcms/${qcm.id}/noter-competences`, {
      candidat_id: cid,
      dossier_id: dossierId,
      candidat_nom: `${candidat.nom || ''} ${candidat.prenom || ''}`.trim(),
      resultats,
      recommandations,
    }, { headers });
    if (onSaved) onSaved();
    return res.data;
  }, [qcm.id, dossierId, headers, onSaved]);

  return <GradingTable competences={qcm.competences_visees || []} candidats={candidats} loadGrades={loadGrades} saveCandidat={saveCandidat} testId={qcm.id} />;
};

// ============ Module Validation Block ============
const EvaluationJournee = ({ dossierId, moduleId, moduleIds, dateJournee, modulesConfirmes, onEvalExist, presences = [], canCreateLibre = false, candidatIds = null }) => {
  const [libreOpen, setLibreOpen] = useState(false);
  const absentIds = useMemo(() => new Set((presences || []).filter(p => p.statut === 'absent').map(p => p.candidat_id)), [presences]);
  const isAbsent = (id) => absentIds.has(id);
  const enAftMap = useMemo(() => Object.fromEntries((presences || []).filter(p => p.statut === 'en_aft').map(p => [p.candidat_id, p.aft_code || 'AFT'])), [presences]);
  const isEnAft = (id) => !!enAftMap[id];
  const [qcmsByModule, setQcmsByModule] = useState({});
  const [evalsByModule, setEvalsByModule] = useState({});
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState({});
  const [evalResults, setEvalResults] = useState({});
  const [qrDialog, setQrDialog] = useState(null);
  const [launching, setLaunching] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [moduleInfoMap, setModuleInfoMap] = useState({});
  const [gradingOpen, setGradingOpen] = useState(null); // eval id currently being graded
  const [qcmGradingOpen, setQcmGradingOpen] = useState(null); // qcm id competency grading open
  const [validations, setValidations] = useState([]);
  const [candidats, setCandidats] = useState([]);
  const [tousCandidats, setTousCandidats] = useState([]);
  const [reportedEvals, setReportedEvals] = useState([]);
  const [extraGradingOpen, setExtraGradingOpen] = useState(null);
  const candidatsPresents = useMemo(() => candidats.filter(c => !isAbsent(c.id) && !isEnAft(c.id)), [candidats, absentIds, enAftMap]); // eslint-disable-line react-hooks/exhaustive-deps
  const absentsDuJour = useMemo(() => tousCandidats.filter(c => isAbsent(c.id)), [tousCandidats, absentIds]); // eslint-disable-line react-hooks/exhaustive-deps
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const moduleIdsKey = (moduleIds?.length > 0 ? moduleIds : moduleId ? [moduleId] : []).join(',');
  const allModuleIds = useMemo(() => (moduleIdsKey ? moduleIdsKey.split(',') : []), [moduleIdsKey]);
  const onEvalExistRef = useRef(onEvalExist);
  useEffect(() => { onEvalExistRef.current = onEvalExist; });

  const fetchData = useCallback(async () => {
    if (allModuleIds.length === 0) { setLoading(false); return; }
    try {
      const newQcmsByModule = {};
      const newEvalsByModule = {};
      const newResults = {};
      const newEvalResults = {};
      const newModuleInfo = {};
      let hasAnyResult = false;

      await Promise.all(allModuleIds.map(async (mid) => {
        try {
          const [qcmRes, evalRes] = await Promise.all([
            axios.get(`${API_URL}/qcms/by-module/${mid}`, { headers }),
            axios.get(`${API_URL}/evaluations-manuelles/by-module/${mid}`, { headers }),
          ]);
          newQcmsByModule[mid] = qcmRes.data;
          newEvalsByModule[mid] = evalRes.data.filter(e => !e.libre || e.dossier_id === dossierId);

          for (const q of qcmRes.data) {
            try {
              const rRes = await axios.get(`${API_URL}/qcms/${q.id}/resultats-dossier/${dossierId}`, { headers, params: dateJournee ? { date_journee: dateJournee } : {} });
              newResults[q.id] = rRes.data;
              if (rRes.data?.passages?.length > 0) hasAnyResult = true;
            } catch (err) { /* silencieux */ }
          }

          for (const ev of newEvalsByModule[mid]) {
            try {
              const rRes = await axios.get(`${API_URL}/evaluations-manuelles/${ev.id}/resultats-dossier/${dossierId}`, { headers, params: dateJournee ? { date_journee: dateJournee } : {} });
              newEvalResults[ev.id] = rRes.data;
              if (rRes.data?.passages?.length > 0) hasAnyResult = true;
            } catch (err) { /* silencieux */ }
          }
        } catch (err) { /* silencieux */ }
      }));

      try {
        const modsRes = await axios.get(`${API_URL}/modules`, { headers });
        for (const m of modsRes.data) {
          if (allModuleIds.includes(m.id)) newModuleInfo[m.id] = m;
        }
      } catch (err) { /* silencieux */ }

      // Load candidates for grading
      try {
        const dossierRes = await axios.get(`${API_URL}/dossiers/${dossierId}`, { headers });
        const estAft = dossierRes.data.type_dossier === 'aft';
        // AFT : seuls les candidats délégués sur cette journée d'examen sont à noter
        // AFT multi-testeurs : candidatIds = candidats que le formateur/testeur connecté examine
        const dCands = (dossierRes.data.candidats || []).filter(c => (!estAft || !dateJournee || (c.jour_passage || (c.dates || [])[0]) === dateJournee) && (!candidatIds || candidatIds.includes(c.candidat_id)));
        const idsDossier = dCands.map(c => c.candidat_id).filter(Boolean);
        const abandonIds = new Set(dCands.filter(c => c.abandon && (!dateJournee || (c.abandon.date || '') <= dateJournee)).map(c => c.candidat_id));
        if (idsDossier.length > 0) {
          const candRes = await axios.get(`${API_URL}/candidats`, { headers });
          setCandidats(candRes.data.filter(c => idsDossier.includes(c.id) && !abandonIds.has(c.id)));
          setTousCandidats(candRes.data.filter(c => idsDossier.includes(c.id)));
        }
      } catch (err) { /* silencieux */ }

      // Load module validations
      try {
        const valRes = await axios.get(`${API_URL}/dossiers/${dossierId}/validations-modules`, { headers });
        setValidations(valRes.data || []);
      } catch (err) { /* silencieux */ }

      // Évaluations reportées manuellement (passages existants hors modules du jour)
      let reported = [];
      try {
        const repRes = await axios.get(`${API_URL}/dossiers/${dossierId}/evaluations-reportees`, { headers });
        reported = repRes.data || [];
        for (const ev of reported) {
          if (!newEvalResults[ev.id]) {
            try {
              const rRes = await axios.get(`${API_URL}/evaluations-manuelles/${ev.id}/resultats-dossier/${dossierId}`, { headers, params: dateJournee ? { date_journee: dateJournee } : {} });
              newEvalResults[ev.id] = rRes.data;
              if (rRes.data?.passages?.length > 0) hasAnyResult = true;
            } catch (err) { /* silencieux */ }
          }
        }
      } catch (err) { /* silencieux */ }
      setReportedEvals(reported);

      setQcmsByModule(newQcmsByModule);
      setEvalsByModule(newEvalsByModule);
      setResults(newResults);
      setEvalResults(newEvalResults);
      setModuleInfoMap(newModuleInfo);
      if (onEvalExistRef.current) onEvalExistRef.current(hasAnyResult);
    } catch (err) { /* silencieux */ }
    setLoading(false);
  }, [dossierId, allModuleIds, headers, dateJournee, candidatIds]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLancer = async (qcm) => {
    setLaunching(qcm.id);
    try {
      await axios.post(`${API_URL}/qcms/lancer`, {
        qcm_id: qcm.id, dossier_id: dossierId, date_journee: dateJournee || '',
      }, { headers });
      toast.success(`QCM "${qcm.titre}" lance pour les candidats`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setLaunching(null);
  };

  const getPublicUrl = (qcmId) => `${APP_URL}/qcm/public?qcm=${qcmId}&dossier=${dossierId}&date=${dateJournee || ''}`;

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Resultats actualises');
  };

  const copyLink = (qcmId) => {
    navigator.clipboard.writeText(getPublicUrl(qcmId));
    toast.success('Lien copie dans le presse-papier');
  };

  if (allModuleIds.length === 0) {
    return (
      <div className="text-center py-6">
        <FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" />
        <p className="text-xs text-slate-400">Selectionnez un module pour voir les evaluations disponibles</p>
      </div>
    );
  }

  if (!modulesConfirmes) {
    return (
      <div className="text-center py-6">
        <FileText className="w-8 h-8 text-amber-200 mx-auto mb-2" />
        <p className="text-xs text-amber-600 font-medium">Confirmez le module du jour pour debloquer les evaluations</p>
      </div>
    );
  }

  if (loading) return <p className="text-xs text-slate-400 text-center py-4">Chargement...</p>;

  const allQcms = Object.values(qcmsByModule).flat();
  const allEvals = Object.values(evalsByModule).flat();
  const hasNoContent = allQcms.length === 0 && allEvals.length === 0;

  const moduleEvalIds = new Set(allEvals.map(e => e.id));
  const extraEvals = [];
  const seenExtra = new Set();
  for (const ev of reportedEvals) {
    if (!moduleEvalIds.has(ev.id) && !seenExtra.has(ev.id)) {
      seenExtra.add(ev.id);
      extraEvals.push(ev);
    }
  }

  // Find the evaluation being graded
  const gradingEval = gradingOpen ? allEvals.find(e => e.id === gradingOpen) : null;

  return (
    <div className="space-y-6" data-testid="evaluation-journee">
      {absentsDuJour.length > 0 && (
        <div className="rounded-lg border border-red-100 bg-red-50/60 px-3 py-2 text-xs text-red-700" data-testid="evaluation-absents-banner">
          <span className="font-semibold">{absentsDuJour.length} candidat(s) absent(s) ce jour — non évaluable(s) :</span>{' '}
          {absentsDuJour.map(c => `${c.nom} ${c.prenom}`).join(', ')}. Ils ne sont pas comptés dans les résultats ni les validations de la journée.
        </div>
      )}
      {hasNoContent && (
        <div className="text-center py-4">
          <FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-xs text-slate-400">Aucune evaluation liee aux modules de cette journee</p>
        </div>
      )}
      {allModuleIds.map(mid => {
        const qcms = qcmsByModule[mid] || [];
        const evalsManuelles = evalsByModule[mid] || [];
        const modInfo = moduleInfoMap[mid];

        if (qcms.length === 0 && evalsManuelles.length === 0) return null;

        return (
          <div key={mid} className="space-y-3">
            {allModuleIds.length > 1 && (
              <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{modInfo?.code || ''}</span>
                <span className="text-sm font-medium text-slate-700">{modInfo?.titre || mid}</span>
              </div>
            )}

            {/* QCMs */}
            {qcms.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="w-3.5 h-3.5 text-indigo-500" />
                  <h4 className="text-xs font-semibold text-slate-600">QCM</h4>
                </div>
                {qcms.map(qcm => {
                  const res = results[qcm.id];
                  const passages = res?.passages || [];
                  const nonPasses = (res?.non_passes || []).filter(np => !isAbsent(np.candidat_id) && !isEnAft(np.candidat_id));
                  const absentsQcm = (res?.non_passes || []).filter(np => isAbsent(np.candidat_id));
                  const enAftQcm = (res?.non_passes || []).filter(np => isEnAft(np.candidat_id));
                  const total = passages.length + nonPasses.length;
                  const nbPasse = passages.length;
                  const notes = passages.filter(p => p.pourcentage != null);
                  const moyenne = notes.length > 0 ? Math.round(notes.reduce((s, p) => s + p.pourcentage, 0) / notes.length) : null;
                  const isRealise = nbPasse > 0;
                  const hasCompetences = (qcm.competences_visees || []).length > 0;
                  const isQcmGradingExpanded = qcmGradingOpen === qcm.id;

                  return (
                    <Card key={qcm.id} className="border-0 shadow-sm" data-testid={`eval-qcm-${qcm.id}`}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-sm font-semibold text-slate-800 truncate">{qcm.titre}</h4>
                              {nbPasse > 0 && qcm.type_evaluation !== 'certification' && <RepasserButton dossierId={dossierId} testType="qcm" testId={qcm.id} titre={qcm.titre} dateJournee={dateJournee} onDone={fetchData} />}
                              {qcm.is_cle && (
                                <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0 font-semibold" data-testid={`badge-cle-qcm-${qcm.id}`}>
                                  <KeyRound className="w-3 h-3 mr-0.5" /> Test clé
                                </Badge>
                              )}
                              {isRealise ? (
                                <span className="inline-flex items-center gap-0.5">
                                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0 font-medium" data-testid={`qcm-status-${qcm.id}`}>
                                    <CheckCircle2 className="w-3 h-3 mr-0.5" /> Realise ({nbPasse})
                                  </Badge>
                                  <VoirResultatsButton testType="qcm" testId={qcm.id} dossierId={dossierId} dateJournee={dateJournee} titre={qcm.titre} />
                                </span>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400 border-slate-200" data-testid={`qcm-status-${qcm.id}`}>Non realise</Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-0.5">{qcm.questions?.length || 0} questions{hasCompetences && ` — ${qcm.competences_visees.length} savoir-faire`}{qcm.type_evaluation && ` — ${qcmTypeLabel(qcm.type_evaluation)}`}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <QcmPdfButtons qcm={qcm} className="h-7 text-[11px]" />
                            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={handleRefresh} disabled={refreshing} data-testid={`btn-refresh-${qcm.id}`}>
                              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => setQrDialog(qcm)} data-testid={`btn-qr-${qcm.id}`}>
                              <QrCode className="w-3.5 h-3.5" /> QR Code
                            </Button>
                            {hasCompetences && (
                              <Button
                                size="sm"
                                className={`h-7 text-[11px] gap-1 ${isQcmGradingExpanded ? 'bg-slate-600 hover:bg-slate-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                                onClick={() => setQcmGradingOpen(isQcmGradingExpanded ? null : qcm.id)}
                                data-testid={`btn-competences-${qcm.id}`}
                              >
                                <Target className="w-3.5 h-3.5" /> {isQcmGradingExpanded ? 'Fermer' : 'Évaluer les savoir-faire et connaissances'}
                              </Button>
                            )}
                            <Button size="sm" className="h-7 text-[11px] gap-1 bg-indigo-600 hover:bg-indigo-700" onClick={() => handleLancer(qcm)} disabled={launching === qcm.id} data-testid={`btn-lancer-${qcm.id}`}>
                              <Send className="w-3.5 h-3.5" /> {launching === qcm.id ? '...' : 'Lancer'}
                            </Button>
                          </div>
                        </div>

                        {/* Inline competency grading for QCM */}
                        {isQcmGradingExpanded && (
                          <QcmGradingTable
                            qcm={qcm}
                            dossierId={dossierId}
                            dateJournee={dateJournee}
                            candidats={candidatsPresents}
                            headers={headers}
                            onSaved={fetchData}
                          />
                        )}

                        {nbPasse > 0 && !isQcmGradingExpanded && (
                          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                            <div className="flex items-center gap-4 text-xs">
                              <span className="flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5" /> {nbPasse}/{total} passe(s)</span>
                              {moyenne !== null && <span className={`font-semibold ${moyenne >= 70 ? 'text-emerald-600' : moyenne >= 50 ? 'text-amber-600' : 'text-red-600'}`}>Moyenne : {moyenne}%</span>}
                            </div>
                            <div className="space-y-1">
                              {passages.map(p => (
                                <div key={p.id} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded">
                                  <CandidatLink dossierId={dossierId} candidatId={p.candidat_id} className="font-medium">{p.candidat_nom}</CandidatLink>
                                  <div className="flex items-center gap-2">
                                    <span className={`font-bold ${p.pourcentage == null ? 'text-slate-400' : p.pourcentage >= 70 ? 'text-emerald-600' : p.pourcentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{p.pourcentage == null ? 'Non répondu' : `${p.pourcentage}%`}</span>
                                    {p.reussi === true && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                                    {p.reussi === false && <XCircle className="w-3.5 h-3.5 text-red-500" />}
                                  </div>
                                </div>
                              ))}
                              {nonPasses.map(np => (
                                <div key={np.candidat_id} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded text-slate-400">
                                  <CandidatLink dossierId={dossierId} candidatId={np.candidat_id}>{np.candidat_nom}</CandidatLink>
                                  <span className="text-[10px] italic">En attente</span>
                                </div>
                              ))}
                              {enAftQcm.map(np => (
                                <div key={np.candidat_id} className="flex items-center justify-between text-xs py-1 px-2 bg-slate-100 rounded text-slate-400 opacity-70" data-testid={`qcm-en-examen-${np.candidat_id}`}>
                                  <CandidatLink dossierId={dossierId} candidatId={np.candidat_id}>{np.candidat_nom}</CandidatLink>
                                  <span className="text-[10px] italic text-cyan-700 font-medium">En examen ({enAftMap[np.candidat_id]}) — évalué sur l'AFT</span>
                                </div>
                              ))}
                              {absentsQcm.map(np => (
                                <div key={np.candidat_id} className="flex items-center justify-between text-xs py-1 px-2 bg-red-50/60 rounded text-slate-400" data-testid={`qcm-absent-${np.candidat_id}`}>
                                  <CandidatLink dossierId={dossierId} candidatId={np.candidat_id}>{np.candidat_nom}</CandidatLink>
                                  <span className="text-[10px] italic text-red-500">Absent — non évalué</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Manual evaluations */}
            {evalsManuelles.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <PenLine className="w-3.5 h-3.5 text-amber-500" />
                  <h4 className="text-xs font-semibold text-slate-600">Evaluations manuelles</h4>
                </div>
                {evalsManuelles.map(ev => {
                  const res = evalResults[ev.id];
                  const passages = res?.passages || [];
                  const nonEvalues = (res?.non_evalues || []).filter(ne => !isAbsent(ne.candidat_id) && !isEnAft(ne.candidat_id));
                  const absentsEval = (res?.non_evalues || []).filter(ne => isAbsent(ne.candidat_id));
                  const enAftEval = (res?.non_evalues || []).filter(ne => isEnAft(ne.candidat_id));
                  const totalCandidats = passages.length + nonEvalues.length;
                  const nbEvalue = passages.length;
                  const isRealise = nbEvalue > 0;
                  const typeLabel = EVAL_TYPE_LABELS[ev.type_evaluation] || ev.type_evaluation;
                  const typeColor = EVAL_TYPE_COLORS[ev.type_evaluation] || 'bg-slate-100 text-slate-700';
                  const isExpanded = gradingOpen === ev.id;

                  return (
                    <Card key={ev.id} className="border-0 shadow-sm" data-testid={`eval-manuelle-${ev.id}`}>
                      <CardContent className="p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`${typeColor} text-[10px] px-1.5 py-0 font-semibold`}>{typeLabel}</Badge>
                              <h4 className="text-sm font-semibold text-slate-800 truncate">
                                {ev.code && <span className="text-slate-400 font-normal mr-1">{ev.code}</span>}
                                {ev.titre}
                              </h4>
                              {ev.libre && <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0 font-semibold" data-testid={`badge-libre-${ev.id}`}>Libre · /{ev.note_max || 20}</Badge>}
                              {(nbEvalue > 0 || nonEvalues.some(ne => ne.repassage)) && ev.type_evaluation !== 'certification' && <RepasserButton dossierId={dossierId} testType="evaluation" testId={ev.id} titre={ev.titre} dateJournee={dateJournee} onDone={fetchData} />}
                              {nonEvalues.some(ne => ne.repassage) && <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0 font-semibold" data-testid={`badge-repassage-eval-${ev.id}`}>{nonEvalues.filter(ne => ne.repassage).length} nouvelle(s) tentative(s) à noter</Badge>}
                              {ev.is_cle && (
                                <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0 font-semibold" data-testid={`badge-cle-eval-${ev.id}`}>
                                  <KeyRound className="w-3 h-3 mr-0.5" /> Test clé
                                </Badge>
                              )}
                              {isRealise ? (
                                <span className="inline-flex items-center gap-0.5">
                                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0 font-medium" data-testid={`eval-status-${ev.id}`}>
                                    <CheckCircle2 className="w-3 h-3 mr-0.5" /> Realise ({nbEvalue}/{totalCandidats})
                                  </Badge>
                                  <VoirResultatsButton testType="evaluation" testId={ev.id} dossierId={dossierId} dateJournee={dateJournee} titre={ev.titre} />
                                </span>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400 border-slate-200" data-testid={`eval-status-${ev.id}`}>Non realise</Badge>
                              )}
                            </div>
                            {ev.competences_visees?.length > 0 && (
                              <p className="text-[10px] text-slate-400 mt-0.5">{ev.competences_visees.length} savoir-faire / connaissance(s)</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            className={`h-7 text-[11px] gap-1 ${isExpanded ? 'bg-slate-600 hover:bg-slate-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                            onClick={() => setGradingOpen(isExpanded ? null : ev.id)}
                            data-testid={`btn-noter-${ev.id}`}
                          >
                            <PenLine className="w-3.5 h-3.5" /> {isExpanded ? 'Fermer' : 'Évaluer les savoir-faire et connaissances'}
                          </Button>
                        </div>

                        {/* Inline grading table */}
                        {isExpanded && (
                          <EvalGradingTable
                            evaluation={ev}
                            dossierId={dossierId}
                            dateJournee={dateJournee}
                            candidats={candidatsPresents}
                            headers={headers}
                            onSaved={fetchData}
                          />
                        )}

                        {/* Summary of results when not expanded */}
                        {!isExpanded && nbEvalue > 0 && (
                          <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                            <div className="flex items-center gap-4 text-xs mb-1">
                              <span className="flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5" /> {nbEvalue}/{totalCandidats} evalue(s)</span>
                              {absentsEval.length > 0 && <span className="text-red-500" data-testid={`eval-absents-${ev.id}`}>{absentsEval.length} absent(s) non évalué(s)</span>}
                              {enAftEval.length > 0 && <span className="text-cyan-700" data-testid={`eval-en-examen-${ev.id}`}>{enAftEval.length} en examen (AFT) — évalué(s) sur l'AFT : {enAftEval.map(x => x.candidat_nom).join(', ')}</span>}
                            </div>
                            {passages.map(p => {
                              const statutColor = p.statut_global === 'A' ? 'text-emerald-600' : p.statut_global === 'ECA' ? 'text-amber-600' : 'text-red-600';
                              const statutBg = p.statut_global === 'A' ? 'bg-emerald-50' : p.statut_global === 'ECA' ? 'bg-amber-50' : 'bg-red-50';
                              return (
                                <div key={p.id} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded">
                                  <CandidatLink dossierId={dossierId} candidatId={p.candidat_id} className="font-medium">{p.candidat_nom}</CandidatLink>
                                  <div className="flex items-center gap-2">
                                    {p.nb_tentatives > 1 && (
                                      <span className="text-[9px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1 py-0.5" title={(p.historique || []).map(h => `Tentative ${h.tentative} : ${h.moyenne ?? '—'} % (${(h.date_journee || '').split('-').reverse().join('/')})${h.retenu ? ' — retenue' : ''}`).join('\n')} data-testid={`eval-tentatives-${p.candidat_id}`}>
                                        T{p.tentative || p.nb_tentatives}/{p.nb_tentatives} · {(p.historique || []).map(h => `${h.moyenne ?? '—'}%`).join(' → ')} · retenu {p.meilleure_moyenne}%
                                      </span>
                                    )}
                                    <span className={`font-bold ${statutColor}`}>{p.moyenne}%</span>
                                    <Badge className={`${statutBg} ${statutColor} text-[10px] px-1.5 py-0 font-semibold`}>{p.statut_global}</Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* La clôture du module (validation groupée) se fait depuis l'onglet « Suivi pédagogique » */}
          </div>
        );
      })}

      {/* Évaluations libres (créées par le formateur) */}
      <div className="space-y-2 pt-2 border-t border-dashed" data-testid="evaluations-libres-section">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <PenLine className="w-3.5 h-3.5 text-indigo-500" />
            <h4 className="text-xs font-semibold text-slate-600">Évaluation libre</h4>
            <span className="text-[10px] text-slate-400">intitulé, objectif, savoir-faire et connaissances → note puis A / ECA / NA</span>
          </div>
          {canCreateLibre && !libreOpen && (
            <Button type="button" size="sm" className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" onClick={() => setLibreOpen(true)} data-testid="btn-nouvelle-eval-libre"><PenLine className="w-3 h-3 mr-1" /> Nouvelle évaluation libre</Button>
          )}
        </div>
        {libreOpen && <EvaluationLibreForm dossierId={dossierId} dateJournee={dateJournee} moduleOptions={allModuleIds.map(mid => ({ id: mid, code: moduleInfoMap[mid]?.code || '', titre: moduleInfoMap[mid]?.titre || '' }))} onCreated={fetchData} onClose={() => setLibreOpen(false)} />}
        {!libreOpen && allEvals.some(e => e.libre) && <p className="text-[11px] text-slate-400">Les évaluations libres apparaissent ci-dessus dans le module rattaché, avec le badge « Libre ».</p>}
        {extraEvals.length > 0 && (
          extraEvals.map(ev => {
            const res = evalResults[ev.id];
            const passages = res?.passages || [];
            const isExpanded = extraGradingOpen === ev.id;
            return (
              <Card key={ev.id} className="border border-indigo-100 shadow-sm" data-testid={`eval-reportee-${ev.id}`}>
                <CardContent className="p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0 font-semibold">Reportée</Badge>
                        <h4 className="text-sm font-semibold text-slate-800 truncate">
                          {ev.code && <span className="text-slate-400 font-normal mr-1">{ev.code}</span>}{ev.titre}
                        </h4>
                        {passages.length > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0 font-medium">{passages.length} noté(s)</Badge>
                        )}
                      </div>
                      {(ev.competences_visees || []).length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-0.5">{ev.competences_visees.length} savoir-faire / connaissance(s)</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className={`h-7 text-[11px] gap-1 ${isExpanded ? 'bg-slate-600 hover:bg-slate-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                      onClick={() => setExtraGradingOpen(isExpanded ? null : ev.id)}
                      data-testid={`btn-noter-reportee-${ev.id}`}
                    >
                      <PenLine className="w-3.5 h-3.5" /> {isExpanded ? 'Fermer' : 'Évaluer les savoir-faire et connaissances'}
                    </Button>
                  </div>
                  {isExpanded && (
                    <EvalGradingTable
                      evaluation={ev}
                      dossierId={dossierId}
                      dateJournee={dateJournee}
                      candidats={candidatsPresents}
                      headers={headers}
                      onSaved={fetchData}
                    />
                  )}
                  {!isExpanded && passages.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                      {passages.map(p => {
                        const statutColor = p.statut_global === 'A' ? 'text-emerald-600' : p.statut_global === 'ECA' ? 'text-amber-600' : 'text-red-600';
                        return (
                          <div key={p.id} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded">
                            <CandidatLink dossierId={dossierId} candidatId={p.candidat_id} className="font-medium">{p.candidat_nom}</CandidatLink>
                            <span className="flex items-center gap-2">
                              {p.nb_tentatives > 1 && (
                                <span className="text-[9px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1 py-0.5" title={(p.historique || []).map(h => `Tentative ${h.tentative} : ${h.moyenne ?? '—'} % (${(h.date_journee || '').split('-').reverse().join('/')})${h.retenu ? ' — retenue' : ''}`).join('\n')} data-testid={`eval-tentatives-${p.candidat_id}`}>
                                  T{p.tentative || p.nb_tentatives}/{p.nb_tentatives} · {(p.historique || []).map(h => `${h.moyenne ?? '—'}%`).join(' → ')} · retenu {p.meilleure_moyenne}%
                                </span>
                              )}
                              <span className={`font-bold ${statutColor}`}>{p.moyenne}% — {p.statut_global}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* QR Code Dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="text-center text-base">QR Code — {qrDialog?.titre}</DialogTitle>
          </DialogHeader>
          {qrDialog && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="bg-white p-4 rounded-xl border-2 border-slate-100">
                <QRCodeSVG value={getPublicUrl(qrDialog.id)} size={220} level="M" includeMargin={false} />
              </div>
              <p className="text-xs text-slate-500 text-center max-w-[250px]">Les candidats scannent ce QR code pour acceder a l'evaluation depuis leur telephone.</p>
              <div className="flex gap-2 w-full">
                <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => copyLink(qrDialog.id)} data-testid="btn-copy-link">
                  <Copy className="w-3 h-3 mr-1" /> Copier le lien
                </Button>
                <Button size="sm" className="flex-1 text-xs" onClick={() => window.open(getPublicUrl(qrDialog.id), '_blank')} data-testid="btn-open-link">
                  <ExternalLink className="w-3 h-3 mr-1" /> Ouvrir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvaluationJournee;
